import { PartialDateRange } from "./misc";

export enum RequestAnalysisStatus {
    Unavailable,
    Available,
    InProgress,
    Completed,
    Failed,
    RetryExceeded,
}

export interface RequestAnalysisData {
    readonly requestType?: string;
    readonly requestPurpose?: string;
    readonly requestTypeMapped?: number;
    readonly requestPurposeMapped?: number;
    /**
     * True when a request type WAS resolved but the extracted purpose was present-yet-not a valid
     * child of it — the "amber / purpose-uncertain" state (TDD §11). Drives the amber ⚠ hint on the
     * Purpose dropdown and flips the Request Type/Purpose row to the amber (LowConfidence) tier until
     * the specialist picks a purpose. Never set when the type itself is unresolved.
     */
    readonly requestPurposeUncertain?: boolean;
    /**
     * AB#35075: the advisory SECOND request-type candidate. Present only when the document plausibly
     * supported two catalog types. Names are for display; the *Mapped ids are the resolved catalog ids.
     * Confidences are the model's self-report - an ordering signal, not calibrated probabilities.
     */
    readonly requestTypeConfidence?: number;
    readonly secondaryRequestType?: string;
    readonly secondaryRequestPurpose?: string;
    readonly secondaryRequestTypeMapped?: number;
    readonly secondaryRequestPurposeMapped?: number;
    readonly secondaryRequestTypeConfidence?: number;
    readonly patientFamilyName?: string;
    readonly patientGivenName?: string;
    readonly patientDateOfBirth?: Date;
    readonly caseNumber?: string;
    readonly clinicName?: string;
    readonly authorizationDate?: Date;
    readonly requestDate?: Date;
    readonly receivedDate?: Date;
    readonly dueDate?: Date;
    readonly serviceDates: readonly PartialDateRange[];
    readonly requestorName?: string;
    readonly requestorAddress1?: string;
    readonly requestorAddress2?: string;
    readonly requestorCity?: string;
    readonly requestorState?: string;
    readonly requestorStateId?: number;
    readonly requestorZipCode?: string;
    readonly requestorContactFamilyName?: string;
    readonly requestorContactGivenName?: string;
    readonly requestorContactFax?: string;
    readonly requestorContactPhone?: string;
    readonly requestorContactEmail?: string;
    readonly requestorSiteCode?: string;
    readonly infoReleaseSections: readonly string[];
    readonly sensitiveInfoDeclarations: readonly string[];
    /**
     * Feature 3 (AB#34099) provider/facility scope. `isScoped` is true only when the request names
     * the records-subject (whose records are being requested) - a general release leaves it false
     * and the values null, and the Provider/Facility row then renders the PRD line 136 "No provider
     * specified" state rather than being hidden (that state is a FEATURE 1 requirement, AB#38161).
     * `multipleSubjects` marks two or more competing subjects: no single scope value is passed
     * downstream and the specialist resolves it.
     *
     * DECLARED AHEAD OF THE API: nothing populates these on the v1.1b branch. Feature 3's read
     * model, extraction and persistence are deferred to August with QA AI 1.2 (#33165 / Spec
     * #34099), so every one of these is undefined here. The fields are declared so the row wiring
     * that Feature 1 needs compiles against the same shape Feature 3 will populate, with no type
     * change required when it lands.
     *
     * AB#38597 SUPERSEDES THE RENDERING CLAIM IN THE FIRST PARAGRAPH. The row is hidden outright on
     * this branch - isQuickEntryRowApplicable returns false for it - so it renders neither the
     * "No provider specified" state nor an unscoped row. Read that method, not this comment, for
     * the current behaviour; the AB#38161 description above is what Feature 3 restores.
     */
    readonly isScoped?: boolean;
    readonly multipleSubjects?: boolean;
    readonly providerName?: string;
    readonly facilityName?: string;
    readonly department?: string;
}

// Confidence + grounding contract (Spec #34100 / F1b-rois). ROIS derives
// nothing it renders: GroundingState is passed through verbatim from the
// F1b-api extraction contract (#34112) and the React UI maps it to a chip
// color.
//
// TDD #31604 (lines 107/187) originally said non-match extraction fields are
// green-if-grounded / red-if-not and NEVER yellow, yellow being reserved for
// F1c #34101 patient/requestor multiple-match: Grounded -> green,
// NotGrounded / InferredNoSource -> red, absent/unmapped -> neutral.
//
// SUPERSEDED FOR THE REQUEST TYPE/PURPOSE ROW ONLY (AB#37979, Joe Widi's ruling
// 2026-08-18; David Heifetz, PR !9995 Important 4 - this comment was the only
// statement of the rule in SPA source and had been left contradicting the code,
// so a reader would have "fixed" the code back to match it):
//
//   * requestType == InferredNoSource  ->  AMBER (LowConfidence) on that row.
//     "In the drop down but not grounded": the value IS present and catalog-mapped,
//     but could not be verified against a located span. Advisory and
//     acknowledge-satisfiable - see RequestTypePurposeField's classificationUngrounded.
//   * requestPurpose == InferredNoSource  ->  still RED, deliberately. AB#35076
//     routes an invented purpose to red so it cannot be acknowledged away without
//     being picked. Amber is looser; these two must not be folded together.
//   * NotGrounded  ->  unchanged. For this row it means the value was ABSENT, which
//     cannot coexist with a catalog-mapped value, so it does not drive amber.
//   * Every OTHER extraction field keeps the original green/red rule above.
export enum GroundingState {
    Grounded = 0,
    NotGrounded = 1,
    InferredNoSource = 2,
}

// ROIS-side, coordinate-only grounding descriptor. The contract's
// FieldGrounding.CoveringLine is source-document text (PHI) and is NEVER
// copied here, returned in the DTO, or logged (AC12, hipaa-compliance.md) -
// this record intentionally has no text member.
export interface FieldGrounding {
    readonly field: string;
    readonly pageNumber: number;
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
}

export interface RequestAnalysis {
    readonly facilityId: number,
    readonly requestBufferId: number,
    readonly associatedDocumentId?: number,
    readonly analysisId?: string,
    readonly createdAtUtc?: Date,
    readonly lastAttemptedAtUtc?: Date,
    readonly completedAtUtc?: Date,
    readonly status: RequestAnalysisStatus,
    readonly data?: RequestAnalysisData,
    readonly dataApprovals?: RequestAnalysisDataApprovals,
    // Per-field confidence, keyed by field name; absent on the persisted
    // re-read path (AC11 - confidence/grounding are read-time/transient only).
    readonly confidence?: Readonly<Record<string, GroundingState>>,
    // Coordinate-only groundings; empty/absent when the Intake-API-side
    // intake.grounding.locatedGate is OFF (AC7/AC9 - tolerate both states).
    readonly fieldGroundings?: readonly FieldGrounding[]
}

export type RequestAnalysisDataApprovals = {
    readonly [key in keyof RequestAnalysisData]?: boolean | null;
};

function tryParseDate(date?: string | null): Date | undefined {
    if (!date || date.startsWith("1900-01-01") || date.startsWith("0001-01-01")) {
        return undefined;
    }
    const output = new Date(date);
    return output.getTime() ? output : undefined;
}

// Coordinate-only projection of the contract grounding (Spec #34100 / F1b-rois).
// Copies ONLY the geometry fields - any CoveringLine (PHI) on the incoming
// object is dropped by construction, never surfaced to the UI (AC12).
function toFieldGrounding(g: Partial<FieldGrounding> & Record<string, unknown>): FieldGrounding {
    return {
        field: g.field ?? "",
        pageNumber: g.pageNumber ?? 0,
        x: g.x ?? 0,
        y: g.y ?? 0,
        width: g.width ?? 0,
        height: g.height ?? 0,
    };
}

export function IntakeFromApiResponse(responseBody?: any): RequestAnalysis | undefined {
    return responseBody && {
        ...responseBody,
        createdAtUtc: tryParseDate(responseBody.createdAtUtc),
        lastAttemptedAtUtc: tryParseDate(responseBody.lastAttemptedAtUtc),
        completedAtUtc: tryParseDate(responseBody.completedAtUtc),
        // Confidence + grounding pass-through, tolerant of absence (contract
        // gap / locatedGate OFF -> undefined, never a throw). Groundings are
        // re-projected coords-only so no CoveringLine can leak through (AC12).
        confidence: responseBody.confidence ?? undefined,
        fieldGroundings: Array.isArray(responseBody.fieldGroundings)
            ? responseBody.fieldGroundings.map(toFieldGrounding)
            : undefined,
        data: responseBody.data && {
            ...responseBody.data,
            patientDateOfBirth: tryParseDate(responseBody.data.patientDateOfBirth),
            authorizationDate: tryParseDate(responseBody.data.authorizationDate),
            requestDate: tryParseDate(responseBody.data.requestDate),
            receivedDate: tryParseDate(responseBody.data.receivedDate),
            dueDate: tryParseDate(responseBody.data.dueDate),
            serviceDates: (responseBody.data.serviceDates as any[]).map(d => ({
                from: tryParseDate(d.from),
                to: tryParseDate(d.to),
            })).filter(d => d.from || d.to) as PartialDateRange[],
            sensitiveInfoDeclarations: (responseBody.data.sensitiveInfoDeclarations as string[]).filter(x => x),
            infoReleaseSections: (responseBody.data.infoReleaseSections as string[]).filter(x => x),
        }
    }
}

export default RequestAnalysis;

// Row anatomy for the shared Quick Entry field-group list renderer (Spec #34098 / F1a).
// Sibling specs (F1b-rois #34100, F1c #34101, F3 #34099) implement their own row
// component against this contract; F1a owns and is the only Spec editing this
// shared portion of models/intake.ts.
export enum FieldGroupRowStatus {
    NeedsEntry = 0,       // empty + required -> sort tier 0
    LowConfidence = 1,    // AI-populated but below confidence threshold -> tier 1
    EmptyNotRequired = 2, // empty + not required -> tier 2
    Confirmed = 3,        // AI-confirmed or user-entered -> tier 3
}

/** One display unit of a FieldGroupRow's value, with the specialist-typed addendum that belongs to it. */
export interface FieldGroupRowValuePart {
    readonly text: string;
    readonly note?: string;
}

export interface FieldGroupRow {
    readonly id: string;                    // stable key, e.g. form field name
    readonly badgeNumber: number;           // 1-based, renumbered on show/hide/reorder
    readonly status: FieldGroupRowStatus;
    readonly label: string;
    /**
     * The row's canonical value, and - importantly - the string committed as SpecialistValue and
     * compared against the AI's answer (DataEntryDetails' buildQuickEntryCorrection inputs).
     * Anything added here for the reader's benefit alone reads downstream as the specialist having
     * corrected the AI, so put it on `displayParts` instead.
     */
    readonly value: string | null;
    /**
     * AB#39380: `value` broken into the units the reader sees, each carrying its own optional
     * specialist-typed addendum - Records Requested uses it for the Other Documents free text.
     * Set only on a row that has an addendum to place; every other row renders `value`.
     *
     * Display only: never committed, never compared (David Heifetz, PR !10721). Each addendum
     * renders in its OWN element, beside the unit it describes and never inside an element
     * carrying the doc-sourced badge or the grounding glyph - those vouch for "this is still the
     * original AI extraction", and the predicate behind them cannot see typed text (Mike
     * Hamilton, PR !10721 rounds 2 and 3). Concatenating the parts must reproduce `value`, which
     * is why both come from one mapper call.
     */
    readonly displayParts?: readonly FieldGroupRowValuePart[];
    /**
     * AB#35075: optional plain-language explanation of WHY the row is in its current status, rendered
     * as a ⓘ next to the collapsed value. Needed because a row's status hint used to live only inside
     * the editable field, i.e. the specialist had to click Edit to find out why a row was amber.
     * Undefined for rows that need no explanation.
     */
    readonly statusExplanation?: string;
    /**
     * AB#35070: set only by getQuickEntryRows when it downgrades an otherwise-Confirmed row to
     * NeedsEntry purely because the row is still open for edit (AB#35073). Without it the Confirm
     * button cannot tell "incomplete, so nothing to confirm" apart from "complete, downgraded
     * because you are looking at it" - they are the same status - and would disable itself on
     * exactly the rows the specialist is trying to confirm.
     */
    readonly isCompleteWhileEditing?: boolean;
    readonly isAiPreSelected: boolean;      // drives the "AI pre-selected" chip; F1a's
                                             // one wired row (Request Type/Purpose) uses
                                             // this only - no confidence-dot styling here,
                                             // that's F1b-rois's (#34100) domain.
    // AB#35289: the STATUS BADGE shows the confirmed/green check when the value is doc-sourced even
    // if isAiPreSelected is false - for the Records Requested + two Service Date rows, whose mappers
    // hardcode isAiPreSelected=false per AB#35066 (they signal AI-sourcing via the inline grounding
    // icon, not the "AI pre-selected" chip). Decouples the badge from the chip: the chip stays keyed
    // on isAiPreSelected ONLY (so it stays off these rows), while the badge reflects doc-sourced.
    readonly isDocSourced?: boolean;
}

// Sort-tier lookup used by the "Needs attention first" toggle.
export const FIELD_GROUP_ROW_SORT_TIER: Readonly<Record<FieldGroupRowStatus, number>> = {
    [FieldGroupRowStatus.NeedsEntry]: 0,
    [FieldGroupRowStatus.LowConfidence]: 1,
    [FieldGroupRowStatus.EmptyNotRequired]: 2,
    [FieldGroupRowStatus.Confirmed]: 3,
};

/**
 * A row counts as "outstanding" (drives N in AC9/AC10) unless it has been
 * verified. Shared between QuickEntryFieldGroupList (renders the rows) and
 * DataEntryDetails.tsx (owns N for the morphing action button, which lives
 * in DataEntryDetailsLeftPane's footer per the mockup's .action-footer -
 * both need the identical definition of "outstanding", not a duplicate.
 */
export function isFieldGroupRowOutstanding(row: FieldGroupRow, verifiedLowConfidenceIds: ReadonlySet<string>): boolean {
    if (row.status === FieldGroupRowStatus.NeedsEntry) {
        return true;
    }
    if (row.status === FieldGroupRowStatus.LowConfidence) {
        return !verifiedLowConfidenceIds.has(row.id);
    }
    return false;
}

/**
 * AB#35121: does this analysis belong to the transaction on screen?
 *
 * The intake redux slice holds ONE analysis with no transaction key, and navigation does not
 * cancel an extraction already in flight (the POST and GET sit under separate `takeLatest`
 * watchers). So a response for transaction A can land in the slice while B is displayed, and
 * every consumer that reads it without asking whose it is will attribute A's data to B.
 *
 * Ownership is already in the payload - RequestAnalysis carries `facilityId` + `requestBufferId`,
 * and a transactionId is exactly `{facilityId}-{requestBufferId}` - so this is a pure data
 * comparison needing no instance state and no observation of fetch transitions. Same idea as
 * transactionDetailsMatchesId, which exists for this class of bug on the transactionDetails slice.
 *
 * An unparseable or absent transactionId returns FALSE deliberately. If the analysis names an
 * owner and we cannot confirm it is the thing on screen, the safe reading is "not ours" - a
 * mid-navigation render where the id is momentarily undefined is exactly when a foreign result
 * must not be attributed to the current screen.
 */
export function analysisBelongsToTransaction(
    analysis: { readonly facilityId?: number; readonly requestBufferId?: number } | undefined,
    transactionId: string | undefined,
): boolean {
    if (!analysis || !transactionId) {
        return false;
    }
    const [facility, buffer] = transactionId.split("-").map(part => parseInt(part, 10));
    if (!Number.isFinite(facility) || !Number.isFinite(buffer)) {
        return false;
    }
    return analysis.facilityId === facility && analysis.requestBufferId === buffer;
}

const EMPTY_ACKNOWLEDGED_IDS: ReadonlySet<string> = new Set<string>();

/** attn = tier-0 (NeedsEntry) + tier-1 (LowConfidence) rows, regardless of verification (Business Rule 10). */
export function isFieldGroupRowAttention(row: FieldGroupRow): boolean {
    return row.status === FieldGroupRowStatus.NeedsEntry || row.status === FieldGroupRowStatus.LowConfidence;
}

/**
 * Bug fix (AB#35091): the Quick Entry header's "{ready} ready to confirm - {attn} need your
 * attention" counts.
 *
 * `ready` used to be "every row that doesn't need attention", which swept in tier 2
 * (EmptyNotRequired - a row that is not applicable to this facility/request, or optional and
 * left empty). Nothing was ever entered or extracted on those rows, so counting them
 * overstated progress: on a total extraction failure the header read "8 ready to confirm - 3
 * need your attention" over a screen where the analysis had failed and nothing was extracted
 * at all. Only rows that hold something confirmable are counted now, so that same screen
 * reads "0 ready to confirm".
 *
 * Consequence, by design: ready + attention is no longer necessarily rows.length. A
 * non-applicable row is neither ready nor outstanding - it is not work.
 *
 * Lives here next to isFieldGroupRowAttention / isFieldGroupRowOutstanding so the header,
 * the Confirm All gate, and the row list all read the same tier definitions.
 */
export function summarizeFieldGroupRows(
    rows: readonly FieldGroupRow[],
    acknowledgedLowConfidenceIds?: ReadonlySet<string>,
): { readonly ready: number; readonly attention: number } {
    const countable = rows.filter(row => row.status !== FieldGroupRowStatus.EmptyNotRequired);
    // Bug fix (AB#38158, defect 3): the header and the footer disagreed on screen. The footer's
    // "Next Action Required (N)" is isFieldGroupRowOutstanding, which subtracts an acknowledged
    // amber row; this header could not, because it was only ever handed `rows`. Acknowledging
    // therefore dropped the footer count while the header still said the row needed attention.
    // The set is OPTIONAL and defaults to empty, so every existing caller (and Business Rule 10's
    // "regardless of verification" reading) is unchanged - only a caller that knows about
    // acknowledgement gets the acknowledgement-aware answer.
    // isFieldGroupRowOutstanding is the footer's own predicate, so the two counts now agree BY
    // CONSTRUCTION rather than by two definitions being kept in step. With an empty set it is
    // exactly isFieldGroupRowAttention (NeedsEntry || LowConfidence), which is what it was.
    const needsAttention = (row: FieldGroupRow) =>
        isFieldGroupRowOutstanding(row, acknowledgedLowConfidenceIds ?? EMPTY_ACKNOWLEDGED_IDS);
    return {
        ready: countable.filter(row => !needsAttention(row)).length,
        attention: countable.filter(needsAttention).length,
    };
}

/**
 * AB#35070: whether the row's Confirm button should be disabled - i.e. the row is genuinely missing
 * its required value, so there is nothing to confirm. Confirming such a row was always a silent
 * no-op (isEditing re-opens anything that isn't Confirmed); this makes the button say so.
 *
 * The subtlety is `isCompleteWhileEditing`. getQuickEntryRows downgrades a COMPLETE row to
 * NeedsEntry while it is open for edit (AB#35073, so it keeps counting as outstanding against
 * Confirm All) - which is precisely the row the specialist is about to confirm. Without that flag
 * this predicate would disable Confirm on exactly those rows and the specialist could never close
 * one. Only NeedsEntry is blocked: LowConfidence is acknowledgeable and EmptyNotRequired is
 * optional, so both keep the button they had.
 */
export function isFieldGroupRowConfirmDisabled(row: FieldGroupRow): boolean {
    return row.status === FieldGroupRowStatus.NeedsEntry && !row.isCompleteWhileEditing;
}
